import React, { useState, useEffect, useCallback } from 'react';
import Dashboard from './components/Dashboard';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export default function App() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE}/summary`);
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const json = await res.json();
      setSummary(json);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return (
    <div style={styles.app}>
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.logo}>🦅</span>
          <div>
            <h1 style={styles.title}>HawkSoft Agency Dashboard</h1>
            <p style={styles.subtitle}>Insurance Agency Management Reports</p>
          </div>
        </div>
        <div style={styles.headerRight}>
          {lastUpdated && (
            <span style={styles.timestamp}>
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button style={styles.refreshBtn} onClick={fetchSummary} disabled={loading}>
            {loading ? '⏳ Loading...' : '🔄 Refresh'}
          </button>
        </div>
      </header>

      <main style={styles.main}>
        {error && (
          <div style={styles.errorBanner}>
            <strong>Connection Error:</strong> {error}
            <br />
            <small>Make sure the backend is running on port 3001</small>
          </div>
        )}
        {loading && !summary ? (
          <div style={styles.loadingState}>
            <div style={styles.spinner}></div>
            <p>Loading dashboard data...</p>
          </div>
        ) : summary ? (
          <Dashboard summary={summary} />
        ) : null}
      </main>

      <footer style={styles.footer}>
        <span>HawkSoft Agency Dashboard v1.0</span>
        <span style={styles.mockBadge}>MOCK MODE</span>
      </footer>
    </div>
  );
}

const styles = {
  app: { minHeight: '100vh', backgroundColor: '#f0f2f5', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", margin: 0 },
  header: { background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', color: '#fff', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  logo: { fontSize: '36px' },
  title: { margin: 0, fontSize: '22px', fontWeight: 700 },
  subtitle: { margin: 0, fontSize: '13px', opacity: 0.7 },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  timestamp: { fontSize: '12px', opacity: 0.6 },
  refreshBtn: { background: '#0f3460', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 },
  main: { maxWidth: '1400px', margin: '0 auto', padding: '24px' },
  errorBanner: { background: '#fff2f0', border: '1px solid #ffccc7', color: '#cf1322', padding: '16px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px' },
  loadingState: { textAlign: 'center', padding: '80px 0', color: '#666' },
  spinner: { width: '40px', height: '40px', border: '4px solid #e8e8e8', borderTop: '4px solid #1a1a2e', borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 1s linear infinite' },
  footer: { textAlign: 'center', padding: '16px', color: '#999', fontSize: '12px', display: 'flex', justifyContent: 'center', gap: '12px', alignItems: 'center' },
  mockBadge: { background: '#faad14', color: '#000', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 700 },
};
